import { api } from '@mca/go-uniapi';
import { caInterface, caTypes, caUtils } from '@mca/contentapi-js';
import { playableMedia, liveSchedule } from '../utils/models';
import { getGvpData, setInstanceSettings, setKeyValues } from './config';
import { toSeconds } from './seek';
import { apiCfg } from '../constants';
import { itemTypes } from '../constants/types';
import { initUA } from './gvp-int.h';

let token;

/**
 * Inits ContentAPI interface.
 */
export async function init() {
  initCA();
  initUA();
  // no hemos hecho nada hasta ahora con la expiración
  const {
    data: {
      Content: { Token }
    }
  } = await loginTrustedDevice();
  token = Token;
}

/**
 * Inits ContentAPI interface.
 */
function initCA() {
  const { caUrl, instanceId, language } = getGvpData();
  caInterface.config({
    host: caUrl,
    instance: instanceId,
    catalog: 'default',
    language: language.replace('_', '-').toLowerCase(),
    timeout: apiCfg.timeoutCA,
    retries: apiCfg.retries
  });
}

/**
 * Obtains Playable Media URL for a given movie id and mediaId.
 *
 * @export
 * @param {number} movieId Movie id.
 * @param {number} mediaId Media id.
 * @returns {Object} Playable Media model.
 */
export function getMediaUrlStb(movieId, mediaId) {
  return api
    .get('PlaybackService.svc/GetMediaUrlStb', {
      token,
      movieId,
      mediaId
    })
    .then((response) => playableMedia(response.data.Content));
}

/**
 * Launches login trusted device.
 * @returns {Promise} To be resolved with token infomation.
 */
function loginTrustedDevice() {
  const { deviceId, deviceType, instanceId, profileId } = getGvpData();
  return api.post('AuthenticationService.svc/LoginTrustedDevice', {
    deviceId,
    deviceType,
    instanceId,
    profileId
  });
}

/**
 * Obtains current live schedule for a given channel id.
 *
 * @export
 * @param {number} channelId Channel id.
 * @returns {Array} Live schedules.
 */
export function getLiveSchedules(channelId) {
  const oneDay = 24 * 60 * 60,
    now = Math.round(toSeconds(Date.now()));
  return caInterface.api
    .rest(
      caTypes.ROUTES.SCHEDULES,
      {
        fields: [
          'Pid',
          'Title',
          'Start',
          'End',
          'ForbiddenTechnology',
          'AgeRatingPid',
          'images.videoFrame'
        ].join(','),
        includeRelations: caTypes.RELATIONS.GENRE,
        includeAttributes: ['ca_blackout_target', 'ca_blackout_areas'].join(',')
      },
      {
        response: {
          formatter: liveSchedule
        }
      }
    )
    .get({
      startTime: now,
      endTime: now + oneDay,
      liveChannelPids: caUtils.toPid(caTypes.CONTENT_TYPES.LIVE_CHANNEL, channelId)
    });
}

/**
 * Gets key values.
 *
 * @returns {Promise} To be resolved with key values.
 */
function getKeyValues(fut) {
  const { instanceId } = getGvpData();
  return api.get('InstanceParameterService.svc/GetKeyValues', {
    partitions: fut,
    components: ['global', 'Chromecast', 'gvp.player'].join(','),
    instanceId
  });
}

/**
 * Obtains the values for FUT support.
 * @return {Promise} with parameter data
 * @private
 */
function getFutSettings(deviceId, deviceType) {
  const { instanceId } = getGvpData();
  return api.get('InstanceParameterService.svc/GetFutSettings', {
    trustedDeviceId: deviceId,
    deviceTypeId: deviceType,
    instanceId
  });
}

/**
 * Gets key values.
 *
 * @returns {Promise} To be resolved with key values.
 */
export async function getFutKeyValues(deviceId, deviceType) {
  try {
    const {
      data: {
        Content: { SettingsPartitions }
      }
    } = await getFutSettings(deviceId, deviceType);
    const {
      data: {
        Content: { Chromecast, global, 'gvp.player': player }
      }
    } = await getKeyValues(SettingsPartitions);
    setKeyValues(global, Chromecast, player);
  } catch (e) {
    const {
      data: {
        Content: { Chromecast, global, 'gvp.player': player }
      }
    } = await getKeyValues();
    setKeyValues(global, Chromecast, player);
  }
}

/**
 * Gets restart url.
 * @param {Object} liveInfo Live info.
 * @param {Number} startTime Time meant to be the beginning of the streaming in ms.
 * @returns {Promise} To be resolved with restart url.
 */
export async function getRestartUrl({ liveChannel, liveSchedule, medias }, startTime) {
  // This padding is used to ensure program start/end are inside the stream seeking range.
  const STREAM_PADDING = 5000;
  const stream = medias[0];
  const {
    data: {
      Content: { Url }
    }
  } = await api.get('EpgService.svc/GetLiveChannelRestartTVUrl', {
    token,
    liveChannelId: liveChannel.id,
    startTime: Math.round(
      toSeconds(liveSchedule.start === startTime ? startTime - STREAM_PADDING : startTime)
    ),
    endTime: toSeconds(liveSchedule.end + STREAM_PADDING),
    streamingType: stream.playType,
    quality: stream.quality
  });
  return Url;
}

/**
 * Gets instance settings.
 * @returns {Promise} To be resolved with instance settings.
 */
export async function getInstanceSettings() {
  const { instanceId, deviceType } = getGvpData();
  const {
    data: {
      Content: { AgeRatings, PlaybackHeartbeatInterval, HeartbeatFrequency }
    }
  } = await api.get('ConfigurationService.svc/GetInstanceSettings', { instanceId, deviceType });
  setInstanceSettings({
    ageRatings: AgeRatings,
    heartBeatInterval: PlaybackHeartbeatInterval,
    heartBeatFrequency: HeartbeatFrequency
  });
}

/**
 * Sends resume data.
 * @param {Object} resume Resume data.
 * @param {Number} id ID.
 * @param {Number} itemType Item type.
 * @returns {Promise} To be resolved.
 */
export function setResumeData(resume, id, itemType) {
  return api.get('PlaybackService.svc/SetItemResumeData', {
    ...resume,
    itemType,
    token,
    itemId: id
  });
}

/**
 * Makes a playback heart beat request.
 *
 * @param {Object} data Heart beat VoD data.
 * @returns {Promise} To be resolved.
 */
export function getPlaybackHeartBeatVod(data) {
  return api.get('PlaybackService.svc/PlaybackHeartBeatVod', { ...data, token });
}

/**
 * Makes a playback heart beat request.
 *
 * @param {Object} data Heart beat live data.
 * @returns {Promise} To be resolved.
 */
export function getPlaybackHeartBeatLive(data) {
  return api.get('PlaybackService.svc/PlaybackHeartBeatLive', { ...data, token });
}

/**
 * Makes a playback heart beat request.
 *
 * @param {Object} data Heart beat recording data.
 * @returns {Promise} To be resolved.
 */
export function getPlaybackHeartBeatRecording(data) {
  return api.get('PlaybackService.svc/PlaybackHeartBeatRecording', { ...data, token });
}

/**
 * Sends events
 *
 * @param {String} eventData List of events.
 * @returns {Promise} To be resolved.
 */
export function addEventBatch(eventData) {
  return api.post('EventService.svc/AddEventBatch', {
    eventData,
    token
  });
}

/**
 * Gets user's token.
 * @returns {string} Token.
 */
export function getToken() {
  return token;
}

/**
 * Sends resume data.
 * @param {Object} resume Resume data.
 * @returns {Promise} To be resolved.
 */
export function setPlaylistResume(resume) {
  return api.get('PlaybackService.svc/SetItemResumeData', {
    ...resume,
    itemType: itemTypes.LSC,
    token
  });
}

/**
 * Gets resume data list of items and selects the one neded.
 * @param {number} id Id of the product.
 * @returns {Promise} To be resolved when finished with delay (s).
 */
export async function getUserItemsResumeData(id) {
  try {
    const {
      data: {
        Content: { List }
      }
    } = await api.get('PlaybackService.svc/GetUserItemsResumeData', { token });
    const delay = List.find(
      ({ ItemId, ItemType }) => ItemId === id && ItemType === itemTypes.LSC
    )?.Time;
    return delay || 0;
  } catch (e) {
    return -1;
  }
}

/**
 * Returns the uniapi server time in unixtimestamp format (seconds).
 * @returns {Promise} The promise returning uniapi time.
 */
export function getUniapiTime() {
  return api
    .get('TimeService.svc/GetTime', undefined, { includeToken: false })
    .then(({ data }) => data.Content);
}
